import { inject, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, input, OnInit, output, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { EspecialidadeService as EspecialidadeCoreService } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { finalize } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { CardContent } from '@/shared/components/card-content/card-content';
import { EspecialidadeRequest } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.model';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ConfirmDialog, ConfirmDialogModule } from 'primeng/confirmdialog';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TagModule } from 'primeng/tag';

export type EspecialidadeListItem = EspecialidadeRequest & {
    _index: number;
    table: {
        displayType: string;
        displayEspecialidade: string;
        displayAreaAtuacao: string;
        displayDate: string;
    };
};

interface EspecialidadeListaRow {
    tipo: { value: string; color: string };
    especialidade: string;
    areaAtuacao: string;
    dataCertificado: string;
    source: EspecialidadeListItem;
}

@Component({
    selector: 'app-especialidade-lista',
    standalone: true,
    imports: [TooltipModule, CardContent, ConfirmDialogModule, ConfirmDialog, BotaoAcoes, CustomTableComponent, CardContainer, TagModule, CanDirective],
    providers: [ConfirmationService],
    templateUrl: './especialidade-lista.component.html',
    styleUrl: './especialidade-lista.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EspecialidadeListaComponent implements OnInit, AfterViewInit {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();
    protected readonly roleEspecialidadeCriar = PERMISSOES.medicos.especialidade.criar;
    rowClick = output<EspecialidadeListItem>();
    criar = output<void>();
    removerRow = output<EspecialidadeListItem>();

    loading = signal<boolean>(false);
    data = signal<EspecialidadeListItem[]>([]);
    acoes = signal<MenuItem[]>([]);

    first = signal(0);
    rows = signal(10);
    totalRecords = computed(() => this.data().length);

    displayedData = computed(() => {
        const start = this.first();
        const end = start + this.rows();
        return this.data().slice(start, end);
    });
    dataTabela = computed<EspecialidadeListaRow[]>(() => this.displayedData().map((item) => this.mapearParaTabela(item)));
    metadataTabela = {
        headers: {
            tipo: { type: 'tag', default: '-', hidden: false, value: 'Tipo' },
            especialidade: { type: 'text', default: '-', hidden: false, value: 'Especialidade' },
            areaAtuacao: { type: 'text', default: '-', hidden: false, value: 'Área de Atuação' },
            dataCertificado: { type: 'text', default: '-', hidden: false, value: 'Data Certificado' }
        },
        title: 'especialidade',
        tag: 'tipo',
        actions: {
            visualizar: {
                label: 'Visualizar',
                icon: 'pi pi-eye',
                color: 'success',
                command: (item: EspecialidadeListaRow) => this.onVisualizarLinha(item.source)
            }
        }
    } satisfies TableMetadata;

    constructor(
        private especialidadeService: EspecialidadeCoreService,
        private cdr: ChangeDetectorRef,
        private confirmationService: ConfirmationService
    ) {
        this.atualizarAcoes();
    }

    ngOnInit(): void {
        this.carregarDados();
    }

    ngAfterViewInit() {
        this.cdr.detectChanges();
    }

    carregarDados() {
        const p = this.pessoa();
        if (!p) return;

        this.loading.set(true);
        this.especialidadeService
            .buscar(p)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (lista) => {
                    const sorted = this.ordenarRegistros(lista);
                    const mapped = sorted.map(
                        (item, index) =>
                            ({
                                ...item,
                                _index: index,
                                table: {
                                    displayType: item.inPrincipal === 'P' ? 'Principal' : 'Secundária',
                                    displayEspecialidade: item.dsEspecialidade || 'N/A',
                                    displayAreaAtuacao: item.dsAreaAtuacao || 'N/A',
                                    displayDate: item.dtCertificado || item.dtEmissao || 'N/A'
                                }
                            }) as EspecialidadeListItem
                    );
                    this.data.set(mapped);
                    this.cdr.markForCheck();
                },
                error: (err) => this.logger.error('Erro ao carregar lista de especialidade', err)
            });
    }

    private ordenarRegistros(lista: EspecialidadeRequest[]): EspecialidadeRequest[] {
        if (!lista) return [];
        const itens = lista.slice();
        const parseDt = (v?: string) => (v ? new Date(v).getTime() : 0);

        return itens.sort((a, b) => {
            const isPrincipalA = a.inPrincipal === 'P';
            const isPrincipalB = b.inPrincipal === 'P';

            if (isPrincipalA && !isPrincipalB) return -1;
            if (isPrincipalB && !isPrincipalA) return 1;

            const da = parseDt(a.dtEmissao ?? a.dtCertificado);
            const db = parseDt(b.dtEmissao ?? b.dtCertificado);
            return db - da;
        });
    }

    handleRowClick(row: EspecialidadeListItem) {
        this.rowClick.emit({ ...row });
    }

    obterEspecialidadeExibicao(item: EspecialidadeListItem): string {
        if (!item) return 'N/A';
        const esp = item.table?.displayEspecialidade || 'N/A';
        return esp.length > 70 ? esp.substring(0, 70) + '...' : esp;
    }

    obterAreaAtuacaoExibicao(item: EspecialidadeListItem): string {
        if (!item) return 'N/A';
        return item.table?.displayAreaAtuacao || 'N/A';
    }

    obterDataCertificadoExibicao(item: EspecialidadeListItem): string {
        if (!item) return 'N/A';
        return this.formatarData(item.table?.displayDate);
    }

    obterRegistroExibicao(item: EspecialidadeListItem): string {
        if (!item) return 'N/A';
        return item.nuRegistro || 'N/A';
    }

    obterLivroFolhaExibicao(item: EspecialidadeListItem): string {
        if (!item) return 'N/A';
        const livro = item.nuLivroRegistro;
        const folha = item.nuFolhaRegistro;
        if (!livro && !folha) return 'N/A';
        if (livro && folha) return `Livro: ${livro} / Folha: ${folha}`;
        if (livro) return `Livro: ${livro}`;
        return `Folha: ${folha}`;
    }

    obterResolucaoExibicao(item: EspecialidadeListItem): string {
        if (!item) return 'N/A';
        return item.resolucao || 'N/A';
    }

    onPageChange(event: { first?: number; rows?: number }) {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? this.rows());
    }

    onCriar(): void {
        this.criar.emit();
    }

    onVisualizarLinha(row: EspecialidadeListItem): void {
        this.rowClick.emit({ ...row });
    }

    onRemoverRow(row: EspecialidadeListItem): void {
        this.confirmationService.confirm({
            message: 'Tem certeza que deseja remover esta especialidade?',
            header: 'Confirmar Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                this.removerRow.emit(row);
            }
        });
    }

    private mapearParaTabela(item: EspecialidadeListItem): EspecialidadeListaRow {
        return {
            tipo: {
                value: item.table.displayType,
                color: item.inPrincipal === 'P' ? 'var(--p-blue-500)' : 'var(--p-purple-500)'
            },
            especialidade: item.table.displayEspecialidade,
            areaAtuacao: item.table.displayAreaAtuacao,
            dataCertificado: this.formatarData(item.table.displayDate),
            source: item
        };
    }

    private formatarData(valor?: string): string {
        if (!valor || valor === 'N/A') return 'N/A';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        return valor;
    }

    private atualizarAcoes(): void {
        this.acoes.set([
            {
                label: 'Criar Novo',
                icon: 'pi pi-plus-circle',
                styleClass: 'bg-green-500',
                command: () => this.onCriar()
            }
        ]);
    }
}
